import React, { useEffect, useState, useCallback } from 'react';
import { CorporateFare } from '@mui/icons-material';
import { Loader } from '@dynamon-as/ui';
import { useOrgEmployeeApi } from '@shared/services';
import { useAuth0 } from '@auth0/auth0-react';
import { useI18n } from '@shared/i18n';
import type { OrgEmployee } from '@shared/types';
import styles from './MyProfile.module.css';

const MyProfile: React.FC = (): React.ReactElement => {
    const { t } = useI18n();
    const [userEmail, setUserEmail] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [orgEmployee, setOrgEmployee] = useState<OrgEmployee>();

    const { get: getOrgEmployee } = useOrgEmployeeApi();
    const { user } = useAuth0();

    const getData = useCallback(async (): Promise<void> => {
        try {
            // Get email from Auth0 user
            const email =
                user?.email ??
                user?.nickname ??
                user?.name ??
                user?.sub ??
                'Ukjent';
            setUserEmail(email);

            setOrgEmployee(await getOrgEmployee());
        } catch (err) {
            console.error('Error in getData:', err);
        } finally {
            setIsLoading(false);
        }
    }, [user, getOrgEmployee]);

    useEffect((): void => {
        getData().catch(console.error);
    }, [getData]);

    const renderInfoRow = (
        title: string,
        value: string | number | undefined,
    ): React.ReactNode => {
        return (
            <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{title}:</span>
                <span className={styles.infoValue}>
                    {value ?? t('profile.unknown')}
                </span>
            </div>
        );
    };

    const formatVacancyRate = (rate: number | undefined): string => {
        if (rate === undefined) return t('profile.unknown');
        const value = rate / 100;
        return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)}%`;
    };

    return (
        <div className={styles.container}>
            {isLoading && (
                <div className={styles.loaderContainer}>
                    <Loader
                        isLoading={isLoading}
                        variant="overlay"
                    />
                </div>
            )}
            {!isLoading && (
                <div className={styles.content}>
                    {/* Page Title */}
                    <h1 className={styles.title}>{t('profile.pageTitle')}</h1>

                    {/* Profile Section */}
                    <div className={styles.section}>
                        <div className={styles.infoGrid}>
                            {renderInfoRow(t('profile.email'), userEmail)}
                        </div>
                    </div>

                    {/* Departments Section */}
                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>
                            <CorporateFare className={styles.sectionIcon} />
                            {t('profile.myDepartments')}
                        </h2>
                        {orgEmployee?.employees?.length ? (
                            <div className={styles.departmentsContainer}>
                                {orgEmployee.employees.map(
                                    (employee, index): React.ReactElement => (
                                        <div
                                            key={index}
                                            className={styles.departmentCard}
                                        >
                                            <h3
                                                className={
                                                    styles.departmentName
                                                }
                                            >
                                                {employee.departmentName ??
                                                    t(
                                                        'profile.unknownDepartment',
                                                    )}
                                            </h3>
                                            <div className={styles.infoGrid}>
                                                {renderInfoRow(
                                                    t('departments.name'),
                                                    employee.name,
                                                )}
                                                {renderInfoRow(
                                                    t(
                                                        'departments.vacancyRate',
                                                    ),
                                                    formatVacancyRate(
                                                        employee.vacancyRate,
                                                    ),
                                                )}
                                                {renderInfoRow(
                                                    t('departments.position'),
                                                    employee.positionName,
                                                )}
                                            </div>
                                        </div>
                                    ),
                                )}
                            </div>
                        ) : (
                            <p className={styles.emptyState}>
                                {t('profile.emptyDepartments')}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyProfile;
